import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 * @property {HTMLElement | undefined} notifyButton - The notify me button.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#openNotifyFromUrl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Opens the STOQ/Restock Rocket 'Notify me' popup for the selected product variant.
   *
   * Per STOQ's own JavaScript API docs (https://docs.stoqapp.com/frontend/docs/javascript-api/),
   * `openInlineForm` is only available on Product Pages — Collection, Home, and Custom pages
   * (which is what quick view on the collection page counts as) only get `openModal`. Rather
   * than showing an embedded themed form on the product page and STOQ's bare native popup
   * everywhere else, we now always use `openModal` and decorate the resulting native popup
   * with the theme's heading/close button, so the popup is the same everywhere.
   *
   * @param {Event} event - The click event
   */
  async onNotifyMe(event) {
    event.preventDefault();

    const target = event.target;
    const button = target instanceof Element ? target.closest('.product-form__notify-button') : null;
    if (!(button instanceof HTMLElement)) return;

    const variantId = button.dataset.variantId || this.refs.variantId?.value;
    const productId = button.dataset.productId || this.dataset.productId;
    const isQuickView = Boolean(this.closest('quick-add-dialog'));

    if (!variantId || !productId) return;

    this.#notifyDebug('click', {
      productId,
      variantId,
      productTitle: button.dataset.productTitle || this.dataset.productTitle,
      variantTitle: button.dataset.variantTitle,
      inQuickAdd: isQuickView,
      productFormSectionId: this.dataset.sectionId,
      productFormProductId: this.dataset.productId,
    });

    const productData = this.#getNotifyProductData(button, productId, variantId);
    const customerData = this.#getRestockRocketCustomerData();
    this.#notifyDebug('product data prepared', {
      id: productData?.id,
      title: productData?.title,
      handle: productData?.handle,
      url: productData?.url,
      variantsCount: Array.isArray(productData?.variants) ? productData.variants.length : 0,
      activeVariant: Array.isArray(productData?.variants)
        ? productData.variants.find((variant) => String(variant.id) === String(variantId))
        : null,
    });

    const restockRocket = await this.#waitForRestockRocket();
    this.#notifyDebug('restock rocket resolved', {
      hasRestockRocket: Boolean(restockRocket),
      hasOpenModal: Boolean(restockRocket?.openModal),
    });

    // Set up (and immediately hide) the theme's own modal shell purely to capture the
    // heading/variant text and to close any open quick-add dialog — the shell itself is
    // never shown; only its text gets copied onto STOQ's decorated native popup below.
    const modal = this.#openNotifyModal(button);
    this.#notifyDebug('modal opened', {
      modalFound: Boolean(modal),
      modalParent: modal?.parentElement?.tagName,
      modalHidden: modal?.classList.contains('hidden'),
    });
    this.closeNotifyModal();

    if (restockRocket?.openModal) {
      restockRocket.openModal(productData, variantId, customerData);
      await this.#bringRestockRocketPopupToFront(modal, variantId);
    } else if (!this.#clickRestockRocketButton(this)) {
      this.#redirectToProductNotify(button, variantId);
    }
  }

  closeNotifyModal(event) {
    event?.preventDefault();

    const modal = this.#getNotifyModal();
    const formContainer = modal?.querySelector('.product-form__notify-form');

    modal?.classList.add('hidden');
    modal?.setAttribute('aria-hidden', 'true');
    formContainer?.replaceChildren();
    this.#ensureBodyVisible();
  }

  #openNotifyModal(button) {
    const modal = this.#getNotifyModal();

    if (!modal) return null;

    const quickAddDialog = this.closest('quick-add-dialog');
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
      quickAddDialog.closeDialog();
    } else {
      this.closest('dialog.quick-add-modal')?.close?.();
    }

    if (modal.dataset.closeListenerAttached !== 'true') {
      modal.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (target.closest('.product-form__notify-close') || target.closest('.product-form__notify-backdrop')) {
          this.closeNotifyModal(event);
        }
      });
      modal.dataset.closeListenerAttached = 'true';
    }

    const title = modal.querySelector('[data-notify-modal-title]');
    const variantText = modal.querySelector('[data-notify-modal-variant]');

    if (title) {
      title.textContent = 'Notify me';
    }

    if (variantText) {
      const productTitle = button.dataset.productTitle || this.dataset.productTitle;
      const variantTitle = button.dataset.variantTitle;
      variantText.textContent = [productTitle, variantTitle].filter(Boolean).join(' - ');
    }

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    modal.style.position = 'fixed';
    modal.style.inset = '0';
    modal.style.width = '100vw';
    modal.style.height = '100vh';
    modal.style.zIndex = '2147483000';
    modal.style.display = 'flex';
    modal.style.alignItems = 'center';
    modal.style.justifyContent = 'center';

    const closeButton = modal.querySelector('.product-form__notify-close');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus({ preventScroll: true });
    }

    return modal;
  }

  #ensureNotifyFormSelector(formContainer, variantId) {
    if (!(formContainer instanceof HTMLElement)) return null;

    formContainer.id = `product-form-stoq-inline-form-${this.dataset.sectionId || 'section'}-${this.dataset.productId || 'product'}-${variantId}`;

    return `#${formContainer.id}`;
  }

  #getNotifyProductData(button, productId, variantId) {
    const productJson = this.querySelector('[data-notify-product-json]')?.textContent;

    if (productJson) {
      try {
        const productData = JSON.parse(productJson);
        productData.id = Number(productId);
        productData.title = productData.title || button.dataset.productTitle || this.dataset.productTitle || undefined;
        productData.handle =
          productData.handle || button.dataset.productHandle || this.dataset.productUrl?.split('/products/')[1]?.split('?')[0];
        productData.url = productData.url || button.dataset.productUrl || this.dataset.productUrl || undefined;
        productData.variants = Array.isArray(productData.variants) ? productData.variants : [];

        const activeVariant = productData.variants.find((variant) => String(variant.id) === String(variantId));
        if (activeVariant) {
          activeVariant.available = false;
          activeVariant.inventory_quantity = 0;
        }

        return productData;
      } catch (error) {
        console.warn('Could not parse notify product JSON.', error);
        this.#notifyDebug('product JSON parse failed', { error, productJson });
      }
    }

    return {
      id: Number(productId),
      title: button.dataset.productTitle || this.dataset.productTitle || undefined,
      handle: button.dataset.productHandle || this.dataset.productUrl?.split('/products/')[1]?.split('?')[0],
      url: button.dataset.productUrl || this.dataset.productUrl || undefined,
      variants: [
        {
          id: Number(variantId),
          available: false,
          inventory_quantity: 0,
        },
      ],
    };
  }

  async #waitForNotifyContainer(selector) {
    if (!selector) return;

    for (let attempt = 0; attempt < 6; attempt++) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const container = document.querySelector(selector);
      if (container instanceof HTMLElement && container.isConnected && container.offsetParent !== null) return;
    }
  }

  async #ensureInlineFormRendered(formContainer) {
    if (!(formContainer instanceof HTMLElement)) return;

    for (let attempt = 0; attempt < 50; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (formContainer.children.length > 0 || formContainer.textContent?.trim()) return;
    }

    throw new Error('STOQ inline form did not render into the notify container.');
  }

  async #moveRestockRocketModalIntoNotifyForm(formContainer, customModal) {
    if (!(formContainer instanceof HTMLElement)) return;

    for (let attempt = 0; attempt < 50; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 100));

      const nativeForm = this.#findRestockRocketForm(customModal);
      if (!nativeForm) {
        const nativeRoot = this.#findRestockRocketModalRoot(customModal);
        if (nativeRoot) {
          this.#decorateRestockRocketModal(nativeRoot, customModal);
          return;
        }
        continue;
      }

      const nativeRoot = this.#getSafeRestockRocketRoot(nativeForm, customModal);

      formContainer.replaceChildren(nativeForm);

      const rootToHide = nativeRoot || this.#findRestockRocketModalRoot(customModal);
      this.#hideRestockRocketRoot(rootToHide, customModal);
      this.#ensureBodyVisible();

      this.#notifyDebug('moved STOQ modal form into quick view popup', {
        nativeFormTag: nativeForm.tagName,
        nativeFormClass: nativeForm.className,
      });
      return;
    }

    throw new Error('STOQ modal form did not render.');
  }

  #decorateRestockRocketModal(nativeRoot, customModal) {
    if (!this.#isSafeRestockRocketRoot(nativeRoot, customModal)) return;

    const customModalElement = customModal instanceof HTMLElement ? customModal : null;
    const customTitle = customModalElement?.querySelector('[data-notify-modal-title]')?.textContent?.trim() || 'Notify me';
    const customVariant = customModalElement?.querySelector('[data-notify-modal-variant]')?.textContent?.trim();

    if (!nativeRoot.querySelector('[data-theme-restock-heading]')) {
      const heading = document.createElement('div');
      heading.dataset.themeRestockHeading = 'true';
      heading.className = 'product-form__notify-native-heading';

      const title = document.createElement('h2');
      title.className = 'product-form__notify-title';
      title.textContent = customTitle;
      heading.appendChild(title);

      if (customVariant) {
        const variant = document.createElement('p');
        variant.className = 'product-form__notify-variant';
        variant.textContent = customVariant;
        heading.appendChild(variant);
      }

      nativeRoot.prepend(heading);
    }

    if (!nativeRoot.querySelector('[data-theme-restock-close]')) {
      const closeButton = document.createElement('button');
      closeButton.dataset.themeRestockClose = 'true';
      closeButton.className = 'button button--unstyled product-form__notify-close product-form__notify-native-close';
      closeButton.type = 'button';
      closeButton.setAttribute('aria-label', 'Close');

      const svgWrapper = document.createElement('span');
      svgWrapper.className = 'svg-wrapper';
      svgWrapper.innerHTML =
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"></path></svg>';
      closeButton.appendChild(svgWrapper);

      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        const nativeClose = nativeRoot.querySelector(
          '[aria-label="Close" i]:not([data-theme-restock-close]), button[class*="close" i]:not([data-theme-restock-close])'
        );

        if (nativeClose instanceof HTMLElement) {
          nativeClose.click();
        } else {
          this.#hideRestockRocketRoot(nativeRoot, customModal);
        }
      });

      nativeRoot.prepend(closeButton);
    }

    nativeRoot.classList.add('product-form__notify-native-dialog');
    customModalElement?.classList.add('hidden');
    customModalElement?.setAttribute('aria-hidden', 'true');

    this.#notifyDebug('decorated STOQ native popup with theme heading', {
      nativeRootTag: nativeRoot.tagName,
      nativeRootClass: nativeRoot.className,
    });
    this.#ensureBodyVisible();
  }

  #hideRestockRocketRoot(nativeRoot, customModal) {
    if (!this.#isSafeRestockRocketRoot(nativeRoot, customModal)) return;

    nativeRoot.style.display = 'none';
    nativeRoot.setAttribute('aria-hidden', 'true');
  }

  #ensureBodyVisible() {
    if (document.body?.style.display === 'none') {
      document.body.style.display = '';
    }
  }

  #closeQuickViewDialog() {
    const quickAddDialog = this.closest('quick-add-dialog') || document.getElementById('quick-add-dialog');

    if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
      quickAddDialog.closeDialog();
      return;
    }

    this.closest('dialog.quick-add-modal')?.close?.();
    document.querySelector('dialog.quick-add-modal[open]')?.close?.();
  }

  /**
   * @param {HTMLElement | null} [customModal] - The theme's own notify modal, kept around
   *   purely so its heading/variant text can be copied onto STOQ's native popup and so it's
   *   excluded from the search for STOQ's own dialog root.
   * @param {string | null} [variantId] - The variant that was actually clicked. STOQ reuses
   *   the same popup DOM across repeat `openModal` calls in a session and does not reset its
   *   own variant dropdown to match the new call, so we force it back in sync here.
   */
  async #bringRestockRocketPopupToFront(customModal = null, variantId = null) {
    for (let attempt = 0; attempt < 20; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 100));

      const popup = this.#findRestockRocketModalRoot(customModal);
      if (!popup) continue;

      popup.style.zIndex = '2147483000';
      popup.style.position = popup.style.position || 'fixed';
      this.#ensureBodyVisible();
      this.#decorateRestockRocketModal(popup, customModal);
      this.#syncRestockRocketVariantSelect(popup, variantId);
      this.#notifyDebug('brought STOQ popup to front', {
        popupTag: popup.tagName,
        popupClass: popup.className,
      });
      return;
    }
  }

  /**
   * Forces STOQ's own variant dropdown (inside its native popup) to match the variant that
   * was actually clicked. Without this, reopening the popup for a different variant in the
   * same session leaves the previous variant selected in STOQ's dropdown.
   * @param {HTMLElement} popup
   * @param {string | null} variantId
   */
  #syncRestockRocketVariantSelect(popup, variantId) {
    if (!variantId) return;

    const select = popup.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) return;
    if (String(select.value) === String(variantId)) return;

    const hasMatchingOption = Array.from(select.options).some((option) => String(option.value) === String(variantId));
    if (!hasMatchingOption) return;

    select.value = String(variantId);
    select.dispatchEvent(new Event('change', { bubbles: true }));

    this.#notifyDebug('synced STOQ variant dropdown', {
      variantId,
      selectedText: select.options[select.selectedIndex]?.text,
    });
  }

  #findRestockRocketModalRoot(customModal) {
    const customModalElement = customModal instanceof HTMLElement ? customModal : null;

    const matchesCandidate = (element) => {
      if (!this.#isSafeRestockRocketRoot(element, customModalElement)) return false;
      if (element.matches('.restock-rocket-button-container-float-right, .restock-rocket-button-cover, .restock-rocket-button-product')) {
        return false;
      }

      const rect = element.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0 && window.getComputedStyle(element).display !== 'none';
      const hasNotifyContent = /notify|email|stock|available|restock/i.test(element.textContent || '');
      const hasFormControl = Boolean(element.querySelector('input, select, textarea, button'));

      return isVisible && hasNotifyContent && hasFormControl;
    };

    // Prefer elements that are actually STOQ's own (id/class containing "restock-rocket" or
    // "stoq"). This avoids false-positives like the theme's own quick-add/cart/search dialogs,
    // which also match a generic "dialog/modal" selector and previously got misidentified and
    // decorated instead of STOQ's real popup.
    const strictRoots = [
      ...document.querySelectorAll('[class*="restock-rocket" i], [id*="restock-rocket" i], [class*="stoq" i], [id*="stoq" i]'),
    ];
    const strictMatch = strictRoots.find(matchesCandidate);
    if (strictMatch) return strictMatch;

    // Fall back to the broader dialog/modal heuristic only if nothing STOQ-specific was found.
    // #isSafeRestockRocketRoot excludes the theme's own dialogs (.dialog-modal, etc.), so this
    // is only meant to catch unusual markup STOQ might render outside its normal wrapper.
    const broadRoots = [
      ...document.querySelectorAll(
        '[role="dialog"], dialog, [class*="modal" i], [id*="modal" i], [class*="restock" i], [id*="restock" i]'
      ),
    ];

    return broadRoots.find(matchesCandidate) || null;
  }

  #getSafeRestockRocketRoot(element, customModal) {
    if (!(element instanceof HTMLElement)) return null;

    let current = element.parentElement;
    while (current && current !== document.body && current !== document.documentElement) {
      if (
        current.matches('[role="dialog"], dialog, [class*="modal" i], [id*="modal" i], [class*="restock" i], [id*="restock" i], [class*="stoq" i], [id*="stoq" i]') &&
        this.#isSafeRestockRocketRoot(current, customModal)
      ) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  #isSafeRestockRocketRoot(element, customModal) {
    if (!(element instanceof HTMLElement)) return false;
    if (element === document.body || element === document.documentElement) return false;

    const customModalElement = customModal instanceof HTMLElement ? customModal : null;
    if (element === customModalElement || customModalElement?.contains(element)) return false;
    if (element.closest('.product-form__notify-modal, .quick-order-list__notify-modal')) return false;

    // Exclude the theme's own dialogs (quick-add/quick-view, cart drawer, search, filters, etc.)
    // so they never get mistaken for STOQ's native popup. All of the theme's own <dialog>
    // elements share the "dialog-modal" class; quick-add-dialog is also excluded directly
    // since its content is fetched/morphed in and could otherwise satisfy the generic
    // "looks like a notify dialog" heuristic (it contains the real notify button/text too).
    if (element.closest('.dialog-modal, quick-add-dialog, cart-drawer-component')) return false;

    return true;
  }

  #findRestockRocketForm(customModal) {
    const customModalElement = customModal instanceof HTMLElement ? customModal : null;
    const restockWrappers = [
      ...document.querySelectorAll('[class*="restock" i], [id*="restock" i], [class*="stoq" i], [id*="stoq" i]'),
    ];
    const candidates = restockWrappers.flatMap((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return [];
      return [wrapper, ...wrapper.querySelectorAll('form, [class*="form" i]')];
    });

    const isUsableForm = (element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (customModalElement?.contains(element)) return false;
      if (element.closest('.product-form__notify-modal, .quick-order-list__notify-modal')) return false;
      if (element.matches('.restock-rocket-button-container-float-right, .restock-rocket-button-cover, .restock-rocket-button-product')) {
        return false;
      }

      const hasField = Boolean(element.querySelector('input, select, textarea'));
      const hasSubmit = Boolean(element.querySelector('button, [type="submit"]'));

      return hasField && hasSubmit;
    };

    const directForm = candidates.find((element) => element instanceof HTMLFormElement && isUsableForm(element));
    if (directForm instanceof HTMLElement) return directForm;

    const wrapper = candidates.find(isUsableForm);
    if (!(wrapper instanceof HTMLElement)) return null;

    return wrapper.querySelector('form') || wrapper;
  }

  #notifyDebug(label, data) {
    console.groupCollapsed(`[Notify me debug] ${label}`);
    console.log(data);
    console.groupEnd();
  }

  #getNotifyModal() {
    return (
      this.querySelector('[data-notify-modal]') ||
      document.querySelector(
        `[data-product-form-notify-modal="${this.dataset.sectionId || ''}-${this.dataset.productId || ''}"]`
      )
    );
  }

  async #waitForRestockRocket() {
    let restockRocket = window._RestockRocket || null;

    for (let attempt = 0; attempt < 40; attempt++) {
      restockRocket = window._RestockRocket || restockRocket;
      if (window._RestockRocket?.openInlineForm || window._RestockRocket?.openModal) return window._RestockRocket;
      await new Promise((resolve) => setTimeout(resolve, 250));
    }

    return window._RestockRocket || restockRocket;
  }

  #clickRestockRocketButton(context = this) {
    const selector = '.restock-rocket-button-cover, .restock-rocket-button-product';
    const searchRoots = [
      context,
      this.closest('quick-add-dialog'),
      this.closest('dialog.quick-add-modal'),
      document,
    ].filter(Boolean);

    const button = searchRoots.reduce((foundButton, root) => {
      if (foundButton instanceof HTMLElement) return foundButton;
      if (!(root instanceof Document) && !(root instanceof Element)) return null;

      const scopedButton = root.querySelector(selector);
      return scopedButton instanceof HTMLElement ? scopedButton : null;
    }, null);

    if (button instanceof HTMLElement) {
      this.#notifyDebug('clicking RestockRocket button fallback', {
        buttonClass: button.className,
        inProductForm: this.contains(button),
        inQuickAddDialog: Boolean(button.closest('quick-add-dialog')),
      });
      button.click();
      return true;
    }

    return false;
  }

  #redirectToProductNotify(button, variantId) {
    const productUrl = button.dataset.productUrl || this.dataset.productUrl;
    this.#notifyDebug('redirecting to product notify fallback', { productUrl, variantId });

    if (!productUrl) return;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('notify_me', '1');
    window.location.href = url.toString();
  }

  #openNotifyFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('notify_me') !== '1') return;

    window.setTimeout(() => {
      const notifyButton = this.querySelector('.product-form__notify-button:not(.hidden)');
      if (notifyButton instanceof HTMLElement) {
        notifyButton.click();
      }
    }, 800);
  }

  #getRestockRocketCustomerData() {
    if (!this.dataset.customerId && !this.dataset.customerEmail) return null;

    return {
      shopify_customer_id: this.dataset.customerId ? Number(this.dataset.customerId) : undefined,
      email: this.dataset.customerEmail || undefined,
      phone: this.dataset.customerPhone || undefined,
      country: this.dataset.customerCountry || undefined,
    };
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    this.#updateNotifyButton(event.detail.resource);

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const currentCustomStock = this.querySelector('[data-custom-stock-wrapper]');
    const newCustomStock = event.detail.data.html.querySelector(
      'product-form-component [data-custom-stock-wrapper]'
    );
    const activeCustomStock = this.#syncCustomStockElement(currentCustomStock, newCustomStock, quantitySelectorWrapper);
    this.#syncCustomStockFallback(event.detail.resource, activeCustomStock, event.detail.data.html);

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        [
          '.product-form__next-stock-date',
          this.querySelector('.product-form__next-stock-date'),
          this.refs.productFormButtons,
        ],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const finalCustomStock = this.#syncCustomStockElement(
      this.querySelector('[data-custom-stock-wrapper]'),
      newCustomStock,
      this.refs.quantitySelectorWrapper || quantitySelectorWrapper
    );
    this.#syncCustomStockFallback(event.detail.resource, finalCustomStock, event.detail.data.html);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  #updateNotifyButton(variant) {
    const notifyButton = this.refs.notifyButton;
    if (!(notifyButton instanceof HTMLElement)) return;

    const showNotify = Boolean(variant && variant.available === false);
    notifyButton.classList.toggle('hidden', !showNotify);

    if (!variant) return;

    notifyButton.dataset.variantId = String(variant.id);
    notifyButton.dataset.variantTitle = variant.title || '';
  }

  #syncCustomStockElement(currentStock, newStock, insertReferenceElement) {
    if (currentStock && newStock) {
      morph(currentStock, newStock);
      return /** @type {HTMLElement | null} */ (currentStock instanceof HTMLElement ? currentStock : null);
    }

    if (currentStock instanceof HTMLElement) {
      return currentStock;
    }

    if (newStock && insertReferenceElement) {
      const clonedStock = /** @type {Element} */ (newStock.cloneNode(true));
      insertReferenceElement.insertAdjacentElement('beforebegin', clonedStock);
      return /** @type {HTMLElement | null} */ (clonedStock instanceof HTMLElement ? clonedStock : null);
    }

    if (insertReferenceElement) {
      const stockWrapper = document.createElement('div');
      stockWrapper.className = 'custom-stock-line';
      stockWrapper.setAttribute('data-custom-stock-wrapper', '');
      stockWrapper.innerHTML = '<span></span>';
      insertReferenceElement.insertAdjacentElement('beforebegin', stockWrapper);
      return stockWrapper;
    }

    return null;
  }

  #syncCustomStockFallback(variant, stockWrapper, sourceHtml = null) {
    if (!(stockWrapper instanceof HTMLElement) || !variant) return;

    const stockText = stockWrapper.querySelector('span');
    if (!(stockText instanceof HTMLElement)) return;

    const variantInputSelector = `variant-picker input[data-variant-id="${variant.id}"]`;
    const selectedVariantInput = /** @type {HTMLInputElement | null} */ (
      this.closest('dialog, .shopify-section, product-card')?.querySelector(variantInputSelector)
    );
    const sourceVariantInput = /** @type {HTMLInputElement | null} */ (
      sourceHtml?.querySelector(variantInputSelector)
    );
    const stockMessage = selectedVariantInput?.dataset.stockMessage || sourceVariantInput?.dataset.stockMessage;
    if (stockMessage) {
      stockText.innerHTML = stockMessage;
      return;
    }

    const stockHtml = stockText.textContent || '';
    if (variant.available === false && /In Stock:/i.test(stockHtml)) {
      stockText.textContent = 'Out of Stock';
      return;
    }

    if (variant.available === false) return;

    const inventoryQuantity = Number(variant.inventory_quantity);
    if (!Number.isFinite(inventoryQuantity)) return;

    stockText.innerHTML =
      inventoryQuantity > 10
        ? 'In Stock: <strong>More than 10 available</strong>'
        : `In Stock: <strong>${inventoryQuantity} Available to Buy</strong>`;
  }

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
